import { useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { usePipeline } from "@/hooks/use-pipeline";
import { useRehab } from "@/hooks/use-rehab";
import { findClosedWonPipeline } from "@/lib/pipeline-display";
import { MockProperty } from "@/data/mockProperties";
import { toast } from "sonner";
import { assertRowsAffected, toUserMessage } from "@/lib/supabase-errors";

export interface PortfolioProperty {
  id: string;
  property: MockProperty;
  notes: string | null;
  purchasePrice: number | null;
  closedAt: string;
}

export function usePortfolio() {
  const {
    pipelines,
    isLoading,
    getPropertiesByPipeline,
    updatePropertyNotes,
    refetch,
  } = usePipeline();
  // `useRehab().projects` already filters out finalized rehabs
  // (`actual_completion IS NOT NULL`), so this set holds only deals that are
  // CURRENTLY in active rehab. Per the design's flow, those live on /rehab
  // and shouldn't surface on /properties until the user clicks
  // "Move to Properties" — which clears them from this set.
  const { projects: rehabProjects } = useRehab();

  const portfolio = useMemo(() => {
    const closedWonPipeline = findClosedWonPipeline(pipelines);
    if (!closedWonPipeline) return [] as PortfolioProperty[];
    const closedWon = getPropertiesByPipeline(closedWonPipeline);
    const activeRehabIds = new Set(
      rehabProjects.map((p) => p.pipeline_property_id),
    );
    return closedWon
      .filter((sp) => !activeRehabIds.has(sp.id))
      .map((sp) => {
        const row = sp as unknown as {
          notes?: string | null;
          purchase_price?: number | null;
        };
        return {
          id: sp.id,
          property: sp.property_data as unknown as MockProperty,
          notes: row.notes ?? null,
          purchasePrice: row.purchase_price ?? null,
          closedAt: sp.updated_at || sp.created_at,
        };
      }) as PortfolioProperty[];
  }, [pipelines, getPropertiesByPipeline, rehabProjects]);

  const updatePortfolioProperty = useCallback(
    async (
      id: string,
      data: { notes: string; purchase_price: number | null },
    ) => {
      const { data: row, error: selectError } = await supabase
        .from("pipeline_properties")
        .select("property_data")
        .eq("id", id)
        .single();

      if (selectError) throw selectError;

      const existing = (row?.property_data ?? {}) as Record<string, Json>;
      const nextPropertyData: Json = {
        ...existing,
        ...(data.purchase_price != null ? { price: data.purchase_price } : {}),
      };

      try {
        const { data: rows, error } = await supabase
          .from("pipeline_properties")
          .update({
            notes: data.notes,
            purchase_price: data.purchase_price,
            property_data: nextPropertyData,
          })
          .eq("id", id)
          .select("id");
        if (error) throw error;
        assertRowsAffected(rows, "update this portfolio property");
        toast.success("Property updated");
        await refetch();
      } catch (err) {
        toast.error(toUserMessage(err, "Failed to update property"));
        throw err;
      }
    },
    [refetch],
  );

  const totalValue = useMemo(
    () => portfolio.reduce((sum, p) => sum + (p.property.price || 0), 0),
    [portfolio],
  );
  const totalRent = useMemo(
    () =>
      portfolio.reduce((sum, p) => sum + (p.property.estimatedRent || 0), 0),
    [portfolio],
  );
  const avgCapRate = useMemo(() => {
    if (portfolio.length === 0) return 0;
    return (
      portfolio.reduce((sum, p) => sum + (p.property.capRate || 0), 0) /
      portfolio.length
    );
  }, [portfolio]);

  return {
    portfolio,
    isLoading,
    totalValue,
    totalRent,
    avgCapRate,
    propertyCount: portfolio.length,
    updatePropertyNotes,
    updatePortfolioProperty,
  };
}
